import React from 'react';
import { cn } from '@/lib/utils';
import { useResponsiveMoveIcon, type MoveIconType } from '@/hooks/ui/use-responsive-move-icon';

export interface StaticDragButtonProps {
  className?: string;
  variant?: 'default' | 'glass' | 'solid';
  size?: 'sm' | 'md' | 'lg';
  iconType?: 'auto' | MoveIconType;
}

/**
 * Static drag button component for ghost cards
 * Always shows the active/hover state without any interactivity
 * Used during drag operations to maintain visual feedback
 */
const StaticDragButton: React.FC<StaticDragButtonProps> = ({
  className,
  variant = 'glass',
  size = 'sm',
  iconType = 'vertical',
}) => {
  const { icon: IconComponent } = useResponsiveMoveIcon(iconType);

  // Size configurations
  const sizeConfig = {
    sm: {
      container: 'w-7 h-7 p-1.5',
      icon: 'h-3 w-3'
    },
    md: {
      container: 'w-8 h-8 p-1.5',
      icon: 'h-4 w-4'
    },
    lg: {
      container: 'w-10 h-10 p-2',
      icon: 'h-5 w-5'
    }
  };

  // Variant configurations - exactly matching the hover state of MoveCardButton
  const variantConfig = {
    default: cn(
      'bg-white/25 dark:bg-black/25', // Hover state background
      'border border-white/35 dark:border-white/25', // Hover state border
      'shadow-sm backdrop-blur-sm'
    ),
    glass: cn(
      'bg-white/30 dark:bg-black/30', // Hover state background
      'border border-white/40 dark:border-white/25', // Hover state border
      'shadow-lg backdrop-blur-lg'
    ),
    solid: cn(
      'bg-gray-150 dark:bg-gray-750', // Hover state background
      'border border-gray-250 dark:border-gray-650', // Hover state border
      'shadow-md'
    )
  };

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full',
        'cursor-grabbing', // Always show grabbing cursor
        'scale-105', // Always scaled up (hover effect)
        'pointer-events-none', // No interaction needed
        'force-hover', // Apply force-hover class for consistency
        sizeConfig[size].container,
        variantConfig[variant],
        className
      )}
      style={{ 
        willChange: 'transform, opacity',
        backfaceVisibility: 'hidden'
      }}
    >
      <IconComponent 
        className={cn(
          sizeConfig[size].icon,
          'text-gray-600 dark:text-gray-300'
        )} 
      />
    </div>
  );
};

export default StaticDragButton; 